import { useContext } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import ItemPriceAndCTAs from '../common/ItemPriceAndCTAs'
import { FlightsFareTypes } from 'constants/flight'
import useFlightFareLabels from 'hooks/Flights/useFlightFareLabels'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import useCanEditTrip from 'tripPlanner/hooks/useCanEditTrip'
import { useLowestFlightPrice } from 'tripPlanner/hooks/api/flights'
import type { FlightItem } from 'tripPlanner/types/tripItem'
import GeoContext from 'contexts/geoContext'
import FormatCurrency from 'components/Common/FormatCurrency'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import { addQueryParamsToPath } from 'lib/url/searchUrlUtils'
import useTripClientSource from 'tripPlanner/hooks/useTripClientSource'
import { buildStandaloneFlightSearchURL } from 'tripPlanner/utils/bookmark/restoreCart'
import TextButton from 'LuxKit/Button/TextButton'
import { addTripIdParam } from 'tripPlanner/utils/url'

interface Props {
  item: FlightItem
  buttonKind: React.ComponentProps<typeof TextButton>['kind']
}

function FlightSearchPriceAndCTAs({ item, buttonKind }: Props) {
  const intl = useIntl()
  const { fareTypeLabelMap } = useFlightFareLabels()
  const canEdit = useCanEditTrip()
  const { currentCurrency, currentRegionCode } = useContext(GeoContext)

  const {
    originAirportCode,
    destinationAirportCode,
    tripId,
    savedItemData: {
      currencyCode,
      regionCode,
      adultPrice,
      fareType,
      totalPrice,
      travellerCounts,
    } = {},
  } = item

  const effectiveCurrencyCode = currencyCode ?? currentCurrency
  const { data: price, isLoading: priceLoading } = useLowestFlightPrice({
    originAirportCode,
    destinationAirportCode,
    currencyCode: effectiveCurrencyCode,
    regionCode: regionCode ?? currentRegionCode,
    shouldQuery:
      !adultPrice && fareType === FlightsFareTypes.RETURN,
  })

  const minAdultPrice = adultPrice ?? price
  const showPrice = !priceLoading && !!minAdultPrice

  const fareTypeLabel = fareType ? fareTypeLabelMap[fareType] : undefined

  const travellerCount: App.Occupants = travellerCounts ?? {
    adults: 0,
  }
  const showTotal = totalPrice && sumUpOccupancies([travellerCount]) > 1

  const priceDisclaimer = intl.formatMessage({
    id: 'kQLx49o-ax9h9fzH-ZwR',
    defaultMessage: '*Price and availability are subject to change.',
    description: 'Disclaimer about flight price and availability in trip planner',
  })

  const source = useTripClientSource()
  const flightSearchUrl = item.savedItemData ? addQueryParamsToPath(
    buildStandaloneFlightSearchURL(item.savedItemData),
    {
      source,
    },
  ) : undefined

  const fareTypeFromLabel = intl.formatMessage(
    {
      id: '_ySJPm0Fzf8Nv0yiYsi6',
      defaultMessage: '{fareType} from',
      description: 'Label showing fare type followed by "from" before the flight price',
    },
    {
      fareType: fareTypeLabel,
    },
  )

  const priceWithAsterisk = intl.formatMessage(
    {
      id: 'F-pC64DD6fpI1z6IzeuT',
      defaultMessage: '{price}*',
      description: 'Flight price with asterisk referencing the price disclaimer',
    },
    {
      price: (
        <FormatCurrency
          value={minAdultPrice ?? 0}
          currency={effectiveCurrencyCode}
          format="dynamicDollar"
        />
      ),
    },
  )

  return (
    <ItemPriceAndCTAs>
      {showPrice && <div className="price-area">
        <Caption variant="medium">{fareTypeFromLabel}</Caption>
        <div>
          <Heading as="span" variant="heading6">
            {priceWithAsterisk}
          </Heading>
          <BodyText as="span" variant="small">
            <FormattedMessage
              id="GSTL22N4w2LZWDCtxIBY"
              defaultMessage=" /adult"
              description="Per adult price suffix label displayed on the flight search detail card in trip planner"
            />
          </BodyText>
          {showTotal && (
            <Caption variant="medium" colour="neutral-three">
              <FormattedMessage
                id="337mnqUHDb9lrnP1cnwL"
                defaultMessage="Total from {price}"
                description="Total flight price label in trip planner in the flights flow"
                values={{ price: <FormatCurrency value={totalPrice} /> }}
              />
            </Caption>
          )}
          <Caption colour="neutral-four" variant="medium">
            {priceDisclaimer}
          </Caption>
        </div>
      </div>}
      {flightSearchUrl && <div className="cta-area">
        <TextButton
          kind={buttonKind}
          to={addTripIdParam(flightSearchUrl, tripId, canEdit)}
          target="_blank"
        >
          {intl.formatMessage({
            id: 'YjGqN_3PzLXI8ny9MJwE',
            defaultMessage: 'Search flights',
            description: 'Button to search for available flights in the trip planner price area',
          })}
        </TextButton>
      </div>}
    </ItemPriceAndCTAs>
  )
}

export default FlightSearchPriceAndCTAs
